import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { schedulerAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Red above this threshold (CLAUDE.md Prompt 22 FIX B spec: "red above
// 1s"). Separate from the watchdog's own WARN/stack-dump thresholds
// (services/loop_lag_watchdog.py) -- this is the diagnostics-card display
// rule, not the instrument's own detection sensitivity.
const RED_THRESHOLD_S = 1.0;

function fmtTimestamp(isoString) {
  if (!isoString) return '-';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  return then.toLocaleString('en-GB');
}

function fmtDuration(seconds) {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  return `${seconds.toFixed(2)}s`;
}

// Diagnostics card: this is the permanent instrument for CLAUDE.md Prompt
// 22 (the 2026-08-16 03:59-04:01 UTC incident where a single-worker uvicorn
// process's event loop was blocked long enough to starve even the
// zero-dependency /health liveness probe, causing a platform restart loop).
// See services/loop_lag_watchdog.py for how a hold is detected (an
// independent OS thread polling a heartbeat coroutine) and
// routes/scheduler.py's /scheduler/event-loop-lag for the read side.
export function EventLoopLagPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: resp } = await schedulerAPI.getEventLoopLag();
      setData(resp);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxHold5min = data?.max_hold_5min_s ?? 0;
  const isRed = maxHold5min >= RED_THRESHOLD_S;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Event Loop Max Hold (5 min)
          {isRed && (
            <Badge variant="destructive" data-testid="event-loop-lag-risk-badge">
              <AlertTriangle className="h-3 w-3 mr-1" />
              loop blocked
            </Badge>
          )}
          {loaded && data && !data.watchdog_running && (
            <Badge variant="secondary" data-testid="event-loop-lag-not-running-badge">
              watchdog not running
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          This backend runs a single worker, so any synchronous call left on the event loop --
          in a request handler, a scheduled job, or a chunked-chain run -- blocks every other
          request, including the /health liveness probe. A hold at or above {RED_THRESHOLD_S}s
          in the last 5 minutes is flagged red below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          data-testid="event-loop-lag-refresh"
          size="sm"
          variant="outline"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>

        {loaded && data && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div
              className={`rounded-lg border p-3 ${isRed ? 'bg-destructive/10 border-destructive/40' : ''}`}
              data-testid="event-loop-lag-max-5min"
            >
              <div className="text-xs text-muted-foreground">Max hold (5 min)</div>
              <div className={`text-lg font-semibold ${isRed ? 'text-destructive' : ''}`}>
                {fmtDuration(maxHold5min)}
              </div>
            </div>
            <div className="rounded-lg border p-3" data-testid="event-loop-lag-max-1hr">
              <div className="text-xs text-muted-foreground">Max hold (1 hr)</div>
              <div className="text-lg font-semibold">{fmtDuration(data.max_hold_1hr_s)}</div>
            </div>
            <div className="rounded-lg border p-3" data-testid="event-loop-lag-count-5min">
              <div className="text-xs text-muted-foreground">Holds (5 min)</div>
              <div className="text-lg font-semibold">{data.hold_count_5min}</div>
            </div>
            <div className="rounded-lg border p-3" data-testid="event-loop-lag-count-1hr">
              <div className="text-xs text-muted-foreground">Holds (1 hr)</div>
              <div className="text-lg font-semibold">{data.hold_count_1hr}</div>
            </div>
          </div>
        )}

        {loaded && data && data.recent_holds.length > 0 && (
          <div className="overflow-x-auto border rounded-lg max-h-64 overflow-y-auto" data-testid="event-loop-lag-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50 sticky top-0">
                  <th className="text-left px-2 py-1 font-medium">When</th>
                  <th className="text-left px-2 py-1 font-medium">Duration</th>
                  <th className="text-left px-2 py-1 font-medium">Culprit frame</th>
                  <th className="text-left px-2 py-1 font-medium">Stacks captured</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_holds.map((h, i) => (
                  <tr
                    key={`${h.at}-${i}`}
                    className={`border-b last:border-0 ${h.duration_s >= RED_THRESHOLD_S ? 'bg-destructive/10' : ''}`}
                  >
                    <td className="px-2 py-1 whitespace-nowrap">{fmtTimestamp(h.at)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmtDuration(h.duration_s)}</td>
                    <td
                      className="px-2 py-1 font-mono max-w-xs truncate"
                      data-testid="event-loop-lag-culprit"
                      title={(h.stacks || [])[(h.stacks || []).length - 1] || ''}
                    >
                      {h.culprit || '-'}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{(h.stacks || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loaded && data && data.recent_holds.length === 0 && (
          <div className="text-xs text-muted-foreground" data-testid="event-loop-lag-empty">
            No event loop holds recorded in the last 14 days.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
